import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UserAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { Button } from "components/buttons";
import { FormLocationSelector, FormDateRangeInput } from "components/form-fields";
import { Modal, ModalSize } from 'components/notifications';
import { Tooltip } from "components/tooltip";
import { Icon, Intent, Position } from "@blueprintjs/core";
import { useSelector, useDispatch } from 'react-redux';
import { SetFiltering } from 'state/actions';
import moment from "moment";
import PropTypes from "prop-types";
import { FleetHubWidget } from "pages/fleet/fleet-hub/FleetHubWidget";
import { FleetService } from "services/FleetService";
import { Key } from 'components/key';
import { PreferencesModal } from "components/preferences-modal/PreferencesModal";
import "./FleetHub.css";

export function FleetHubOverview({
    locationList,
    loading = false,
    selectedLocations,
    setSelectedLocations
})
{
    const fleetView = "FMS:View";
    const eventHubReduxName = "eventHubReduxHUB";
    const eventListingFilterReduxName = "eventListingFilterReduxHUB";

    const weekDateDefault = 6;
    const initialStartDate = moment().startOf('isoWeek').subtract(weekDateDefault, 'weeks');
    const initialEndDate = moment().startOf('isoWeek').add(weekDateDefault, 'weeks');
    const yearsDateDefault = 5;
    const minDate = moment().subtract(yearsDateDefault, 'years').toDate();
    const maxDate = moment().add(yearsDateDefault, 'years').toDate();
    const periodSelectorDateFormat = "DD MMM YYYY";
    const dateFormat = "YYYY-MM-DD";
    const dispatch = useDispatch();
    var navigate = useNavigate();
    const [eventStatusOptions, setEventStatusOptions] = useState([]);
    const [statusGroups, setStatusGroups] = useState({
        upcoming: [],
        inProgress: [],
        completed: []
    });
    const fhdLoadingDefault1 = 1;
    const fhdLoadingDefault2 = 2;
    const fhdLoadingDefault3 = 3;
    const fhdLoadingDefault4 = 4;
    const fhdLoadingDefault5 = 5;
    const fhdLoadingDefault6 = 6;

    const [reduxLoaded, setReduxLoaded] = useState(false);
    const [startDate, setStartDate] = useState(initialStartDate.format(periodSelectorDateFormat));
    const [endDate, setEndDate] = useState(initialEndDate.format(periodSelectorDateFormat));
    const [locationDefaultsToTrue, setLocationDefaultsToTrue] = useState(false);
    const [fleetHubData, setFleetHubData] = useState([fhdLoadingDefault1, fhdLoadingDefault2, fhdLoadingDefault3, fhdLoadingDefault4, fhdLoadingDefault5, fhdLoadingDefault6]);
    const [fleetHubDataLoading, setFleetHubDataLoading] = useState(false);
    const [fleetHubDataError, setFleetHubDataError] = useState(null);
    const [isEventStatusModalOpen, setIsEventStatusModalOpen] = useState(false);
    const [showPreferencesModal, setShowPreferencesModal] = useState(false);
    const [widgets, setWidgets] = useState([]);
    const [preferencesUpdated, setPreferencesUpdated] = useState(false);

    const eventHubRedux = useSelector(state => state.filters[eventHubReduxName]);

    useEffect(() => {
        //We know the redux store has loaded, we only want to set the offset once
        if (!reduxLoaded) {
            setReduxLoaded(true);
            if (eventHubRedux) {
                setStartDate(eventHubRedux.startDate);
                setEndDate(eventHubRedux.endDate);
                setSelectedLocations(eventHubRedux.locations);
            } else {
                setLocationDefaultsToTrue(true);
            }
        }
    }, [eventHubRedux]);

    useEffect(() => {
        if (reduxLoaded && selectedLocations) {
            setFleetHubDataLoading(true);
            setFleetHubDataError(null);
            const locationIds = Array.isArray(selectedLocations)
                ? selectedLocations.map(l => (typeof l === 'object' && l !== null && l.id !== undefined ? l.id : l))
                : [];

            FleetService.getFleetHubOverview(
                startDate ? moment(startDate).toISOString() : null,
                endDate ? moment(endDate).toISOString() : null,
                locationIds
            )
                .then(data => setFleetHubData(data))
                .catch(err => setFleetHubDataError(err))
                .finally(() => setFleetHubDataLoading(false));
        }
    }, [reduxLoaded, startDate, endDate, selectedLocations, preferencesUpdated]);

    useEffect(() => {
        FleetService.getEventOptions().then(options => {
            const statuses = options.eventStatuses ?? [];
            setEventStatusOptions(statuses);
            setStatusGroups({
                upcoming: statuses.filter(s => s.key === "PLANNED"
                    || s.key === "SCHEDULED").map(s => s.id),
                inProgress: statuses.filter(s => s.key === "INPROGRESS"
                    || s.key === "INPROGRESSFAILED").map(s => s.id),
                completed: statuses.filter(s => s.key === "COMPLETED"
                    || s.key === "FAILED"
                    || s.key === "COMPLETEDWITHISSUES").map(s => s.id)
            });
        });
    }, []);

    function setFleetHubDispatch(newLocations, newStartDate, newEndDate) {
        dispatch(SetFiltering(eventHubReduxName, {
            startDate: newStartDate ?? startDate,
            endDate: newEndDate ?? endDate,
            locations: newLocations ?? selectedLocations
        }));
    }

    function onSelectedLocationsChange(locations) {
        setSelectedLocations(locations);
        setFleetHubDispatch(locations, null, null);
    }

    function onDateRangeChange(item) {
        const tempStartDate = item.startDate.date ? item.startDate.date.format(dateFormat) : "";
        const tempEndDate = item.endDate.date ? item.endDate.date.format(dateFormat) : "";

        setStartDate(tempStartDate ? moment(tempStartDate, dateFormat).format(periodSelectorDateFormat) : "");
        setEndDate(tempEndDate ? moment(tempEndDate, dateFormat).format(periodSelectorDateFormat) : "");
        setFleetHubDispatch(null, tempStartDate, tempEndDate);
    }

    function handleWidgetNavigate(eventTypeId, eventTypeName, eventStatusIds) {
        let chips = [];
        const params = new URLSearchParams();
        if (eventTypeId > 0) {
            //The all events has an eventTypeId of -1, so we want to show all events
            chips.push({
                    name: "EVENTTYPEID",
                    value: String(eventTypeId),
                    displayName: eventTypeName || "Event Type"
                });
            params.set("eventTypeId", String(eventTypeId));
        }

        if (eventStatusIds && eventStatusIds.length > 0) {
            chips.push(...eventStatusIds.map(id => {
                const statusName = eventStatusOptions.find(s => String(s.id) === String(id))?.name;
                return {
                    name: "EVENTSTATUSID",
                    value: String(id),
                    displayName: statusName || "Event Status"
                };
            }))
            params.set("eventStatusId", (eventStatusIds ?? []).map(String).join(","));
        };

        dispatch(SetFiltering(eventListingFilterReduxName, chips));
        let url = chips.length > 0 ? `/fleet/events?${params.toString()}` : "/fleet/events";
        navigate(url);
    }

    function openPreferencesModal() {
        setShowPreferencesModal(true);
        FleetService.getHubDisplayPreferences()
            .then(data => {
                if (Array.isArray(data)) {
                    setWidgets(
                        data
                            .toSorted((a, b) => a.order - b.order)
                            .map(item => ({
                                ...item,
                                id: item.eventTypeId,
                                name: item.eventTypeName,
                                userPriority: item.order
                            }))
                    );
                } else {
                    setWidgets([]);
                }
            })
            .catch(() => setWidgets([]));
    }

    function closePreferencesModal() {
        setShowPreferencesModal(false);
    }

    function saveFleetHubPreferences(preferences) {
        let displayOptions = preferences.map(item => ({
                eventTypeId: item.id,
                userSelected: item.userSelected
            }));
        return FleetService.saveHubDisplayPreferences({ displayOptions });
    }

    const handleSavePreferences = (newPreferences) => {
        setWidgets(newPreferences);
        setPreferencesUpdated(prev => !prev);
    };

    let fleetHubContent;
    if (!fleetHubDataLoading && !loading && (fleetHubDataError || ((Array.isArray(fleetHubData) && fleetHubData.length === 0)))) {
        fleetHubContent = (
            <div>
                <h2>Nothing to see here...</h2>
                <div>No results found, please try a different date range.</div>
            </div>
        );
    } else if (fleetHubData && Array.isArray(fleetHubData) && fleetHubData.length > 0) {
        fleetHubContent = (
            <div className="fleet-hub-widgets-list">
                {fleetHubData.map(item => (
                    <FleetHubWidget
                        key={item.eventTypeId}
                        eventTypeId={item.eventTypeId}
                        iconName={item.eventIcon}
                        eventTypeName={item.eventTypeName}
                        eventSummary={item}
                        onNavigate={(eventTypeId, groupKey) => {
                            handleWidgetNavigate(item.eventTypeId, item.eventTypeName, statusGroups[groupKey] ?? []);
                        }}
                        eventStatuses={eventStatusOptions}
                        loading={loading || fleetHubDataLoading}
                    />
                ))}
            </div>
        );
    }

    return (
        <UserAccess
            perform={fleetView}
            yes={() => (
                <div>
                    <div className="inline-items spacer-bottom">
                        <FormDateRangeInput
                            id="listing-period"
                            startDate={startDate}
                            endDate={endDate}
                            onChange={onDateRangeChange}
                            disabled={loading || fleetHubDataLoading}
                            allowClear={false}
                            minDate={minDate}
                            maxDate={maxDate}
                            large
                        />
                        <FormLocationSelector
                            businessArea=""
                            loading={loading || fleetHubDataLoading}
                            selectedLocations={selectedLocations}
                            setSelectedLocations={onSelectedLocationsChange}
                            useHierarchy={false}
                            useCustomLocations
                            customLocations={locationList}
                            useLocationDefaults={locationDefaultsToTrue}
                            locationDefaultsToTrue={locationDefaultsToTrue}
                        />
                        <Tooltip
                            position={Position.RIGHT}
                            content={
                                <div>
                                    <h4>Location Filter and Dates</h4>
                                    <p>
                                        The location filter and selected dates are applied to all the fleet overview pages.
                                    </p>
                                </div>
                            }
                        >
                            <Icon
                                className="hub-tool-tip"
                                icon="info-sign"
                                size={15}
                                color="#0C2235"
                            />
                        </Tooltip>
                        <Button
                            intent="secondary"
                            icon="properties"
                            iconOnly
                            id="event-status-modal-btn"
                            onClick={() => setIsEventStatusModalOpen(true)}
                            className="key-button"
                            large={false}
                            disabled={loading || fleetHubDataLoading}
                            loading={loading || fleetHubDataLoading}
                        />
                        <Button
                            text="Widget Preferences"
                            minimal={true}
                            large={false}
                            intent="secondary"
                            icon="cog"
                            id="configure-properties"
                            onClick={openPreferencesModal}
                            loading={loading}
                        />
                        <Modal
                            isOpen={isEventStatusModalOpen}
                            onClose={() => setIsEventStatusModalOpen(false)}
                            title="Event Status"
                            size={ModalSize.SMALL}
                        >
                            <div>
                                <div className="spacer-bottom">
                                    
                                    <div className="col">
                                        <h5 className="key">Upcoming events</h5>
                                        <Key
                                            loading={loading || fleetHubDataLoading}
                                            items={eventStatusOptions
                                                .filter(s => statusGroups.upcoming.includes(s.id))
                                                .map(s => ({ ...s, colours: s.statusColour }))
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                            }
                                            listVertical
                                        />
                                    </div>
                                    
                                    <div className="col spacer-left">
                                        <h5 className="key">In Progress events</h5>
                                        <Key
                                            loading={loading || fleetHubDataLoading}
                                            items={eventStatusOptions
                                                .filter(s => statusGroups.inProgress.includes(s.id))
                                                .map(s => ({ ...s, colours: s.statusColour }))
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                            }
                                            listVertical
                                        />
                                    </div>
                                    
                                    <div className="col spacer-left">
                                        <h5 className="key">Completed Events</h5>
                                        <Key
                                            loading={loading || fleetHubDataLoading}
                                            items={eventStatusOptions
                                                .filter(s => statusGroups.completed.includes(s.id))
                                                .map(s => ({ ...s, colours: s.statusColour }))
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                            }
                                            listVertical
                                        />
                                    </div>
                                </div>
                            </div>
                        </Modal>
                        <PreferencesModal
                            isOpen={showPreferencesModal}
                            onClose={closePreferencesModal}
                            displayPreferences={widgets}
                            cancelButtonIntent={Intent.NONE}
                            onSave={handleSavePreferences}
                            onPreferencesSaved={() => setPreferencesUpdated(prev => !prev)}
                            onLoadPreferences={FleetService.getHubDisplayPreferences}
                            onSavePreferences={saveFleetHubPreferences}
                            preferencesKey={"FleetHub"}
                            preferenceTypeText={"Widget"}
                        />
                    </div>

                    {fleetHubContent}
                </div>
            )}
            no={() => <PageRestricted />}
        />
    );
}

FleetHubOverview.propTypes = {
    locationList: PropTypes.object,
    loading: PropTypes.bool,
    selectedLocations: PropTypes.array,
    setSelectedLocations: PropTypes.func
};